import { useState } from 'react';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { TYPES_TITRE_TRANSPORT, libelleTitreTransport } from '../lib/contrat';
import { formaterDate, formaterMontant } from '../lib/format';
import { Badge, Champ, Infobulle } from './Elements';
import type { TransportContrat, TypeTitreTransport } from '../types';

/**
 * Les TITRES DE TRANSPORT d'un contrat, par periode (2026-11-03, demande
 * de Georges) : un pass Navigo, des titres unitaires ou un forfait
 * Imagine R, du ... au ..., pour un montant mensuel AU PRIX DU TITRE. La
 * part remboursee ne s'affiche plus ici (2026-11-04) : elle depend du
 * temps de travail du mois, et c'est la paie qui la dit.
 *
 * Meme grammaire que le salaire mensuel (SalairesContrat) : une periode
 * sans fin vaut pour les mois a venir, en ajouter une ferme la precedente
 * a la veille, et une periode qui a servi a un mois de paie cloture ne se
 * modifie plus. Le bloc ecrit immediatement — chaque geste est un acte a
 * part —, sans passer par le bouton Enregistrer du contrat.
 *
 * Le JUSTIFICATIF est une case (2026-11-04, demande de Georges) : fourni
 * ou absent, cochee directement dans le tableau, verrouillage ou pas — ce
 * n'est pas ce qui a ete verse. Le serveur la remet a « absent » a chaque
 * 1er septembre : le salarie justifie son titre chaque annee scolaire.
 */

const enEuros = (centimes: number) => (centimes / 100).toFixed(2).replace('.', ',');
const enCentimes = (texte: string): number | null => {
  const n = Number(texte.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) && n >= 0 && texte.trim() !== '' ? Math.round(n * 100) : null;
};


export function TransportsContrat({ contratId, peutGerer }: { contratId: string; peutGerer: boolean }) {
  const transports = useRessource(
    () => api.get<TransportContrat[]>(`/api/contrats/${contratId}/transports`),
    [contratId],
  );
  const vide = { type: 'navigo' as TypeTitreTransport, montant: '', dateDebut: '', dateFin: '', justificatif: false };
  const [saisie, setSaisie] = useState(vide);
  const [edition, setEdition] = useState<{
    id: string;
    type: TypeTitreTransport;
    montant: string;
    dateFin: string;
  } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const agir = async (travail: () => Promise<unknown>) => {
    setErreur(null);
    setEnCours(true);
    try {
      await travail();
      transports.recharger();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const ajouter = () => {
    const montant = enCentimes(saisie.montant);
    if (montant === null || !saisie.dateDebut) {
      setErreur('Indiquez un montant mensuel et une date de début.');
      return;
    }
    void agir(async () => {
      await api.post(`/api/contrats/${contratId}/transports`, {
        type: saisie.type,
        montant,
        dateDebut: saisie.dateDebut,
        dateFin: saisie.dateFin || null,
        justificatif: saisie.justificatif,
      });
      setSaisie(vide);
    });
  };

  const enregistrerEdition = () => {
    if (!edition) return;
    const montant = enCentimes(edition.montant);
    if (montant === null) {
      setErreur('Montant invalide : utilisez le format 90,80.');
      return;
    }
    void agir(async () => {
      await api.patch(`/api/contrats/${contratId}/transports/${edition.id}`, {
        type: edition.type,
        montant,
        dateFin: edition.dateFin || null,
      });
      setEdition(null);
    });
  };

  /** Cocher ou decocher ecrit tout de suite : un geste, pas un formulaire. */
  const basculerJustificatif = (t: TransportContrat) =>
    void agir(() =>
      api.patch(`/api/contrats/${contratId}/transports/${t.id}`, { justificatif: !t.justificatif }),
    );

  const supprimer = (t: TransportContrat) => {
    if (!window.confirm('Supprimer cette période de titre de transport ?')) return;
    void agir(() => api.delete(`/api/contrats/${contratId}/transports/${t.id}`));
  };

  const aujourdhui = new Date().toISOString().slice(0, 10);
  const enCoursDeValidite = (t: TransportContrat) =>
    t.date_debut <= aujourdhui && (t.date_fin === null || t.date_fin >= aujourdhui);

  // La periode la plus recente en tete, l'historique dessous.
  const lignes = [...(transports.donnees ?? [])].sort((a, b) => b.date_debut.localeCompare(a.date_debut));

  const choixType = (valeur: TypeTitreTransport, onChange: (t: TypeTitreTransport) => void) => (
    <select
      className="champ py-1.5"
      aria-label="Type de titre"
      value={valeur}
      onChange={(e) => onChange(e.target.value as TypeTitreTransport)}
    >
      {TYPES_TITRE_TRANSPORT.map((t) => (
        <option key={t.valeur} value={t.valeur}>
          {t.libelle}
        </option>
      ))}
    </select>
  );

  return (
    <div className="space-y-3">
      <div className="etiquette">Titres de transport</div>

      {peutGerer && (
        <div className="grid items-end gap-2 grid-cols-[9rem_7rem_9rem_9rem_1fr_auto]">
          <Champ libelle="Titre">{choixType(saisie.type, (type) => setSaisie({ ...saisie, type }))}</Champ>
          <Champ libelle="Montant / mois (€)">
            <input
              className="champ tabulaire py-1.5 text-right"
              placeholder="90,80"
              inputMode="decimal"
              value={saisie.montant}
              onChange={(e) => setSaisie({ ...saisie, montant: e.target.value })}
            />
          </Champ>
          <Champ libelle="Du">
            <input
              type="date"
              className="champ py-1.5"
              value={saisie.dateDebut}
              onChange={(e) => setSaisie({ ...saisie, dateDebut: e.target.value })}
            />
          </Champ>
          <Champ libelle="Au">
            <input
              type="date"
              className="champ py-1.5"
              value={saisie.dateFin}
              min={saisie.dateDebut || undefined}
              onChange={(e) => setSaisie({ ...saisie, dateFin: e.target.value })}
            />
          </Champ>
          <Champ libelle="Justificatif">
            <label className="flex h-[2.125rem] items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={saisie.justificatif}
                onChange={(e) => setSaisie({ ...saisie, justificatif: e.target.checked })}
              />
              {saisie.justificatif ? 'Fourni' : 'Absent'}
            </label>
          </Champ>
          <div className="block">
            <span className="etiquette invisible">Ajouter</span>
            <button type="button" className="bouton-secondaire block py-1.5" disabled={enCours} onClick={ajouter}>
              Ajouter
            </button>
          </div>
        </div>
      )}

      {(erreur ?? transports.erreur) && <p className="text-sm text-red-700">{erreur ?? transports.erreur}</p>}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-encre-500">
            <th className="py-1 pr-2 font-medium">Titre</th>
            <th className="py-1 pr-2 text-right font-medium">Montant / mois</th>
            <th className="py-1 pr-2 font-medium">Du</th>
            <th className="py-1 pr-2 font-medium">Au</th>
            <th className="py-1 pr-2 font-medium">Justificatif</th>
            <th />
          </tr>
        </thead>
        <tbody className="divide-y divide-encre-100">
          {lignes.length === 0 && !transports.chargement && (
            <tr>
              <td colSpan={6} className="py-2 text-encre-400">
                Aucun titre saisi : rien ne sera remboursé au titre du transport.
              </td>
            </tr>
          )}
          {lignes.map((t) =>
            edition?.id === t.id ? (
              <tr key={t.id} className="bg-encre-50">
                <td className="py-1.5 pr-2">{choixType(edition.type, (type) => setEdition({ ...edition, type }))}</td>
                <td className="py-1.5 pr-2">
                  <input
                    className="champ tabulaire py-1 text-right"
                    aria-label="Montant mensuel"
                    value={edition.montant}
                    onChange={(e) => setEdition({ ...edition, montant: e.target.value })}
                  />
                </td>
                <td className="tabulaire py-1.5 pr-2 text-encre-500">{formaterDate(t.date_debut)}</td>
                <td className="py-1.5 pr-2">
                  <input
                    type="date"
                    className="champ py-1"
                    value={edition.dateFin}
                    min={t.date_debut}
                    onChange={(e) => setEdition({ ...edition, dateFin: e.target.value })}
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <CaseJustificatif transport={t} peutGerer={peutGerer} enCours={enCours} onBasculer={basculerJustificatif} />
                </td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  <button type="button" className="text-xs underline" disabled={enCours} onClick={enregistrerEdition}>
                    Enregistrer
                  </button>
                  <button type="button" className="ml-2 text-xs text-encre-500 underline" onClick={() => setEdition(null)}>
                    Annuler
                  </button>
                </td>
              </tr>
            ) : (
              <tr key={t.id} className={t.verrouillee ? 'text-encre-400' : ''}>
                <td className="py-1.5 pr-2">
                  {libelleTitreTransport(t.type)}
                  {enCoursDeValidite(t) && (
                    <span className="ml-2">
                      <Badge ton="succes">en cours</Badge>
                    </span>
                  )}
                </td>
                <td className="tabulaire py-1.5 pr-2 text-right">{formaterMontant(t.montant)}</td>
                <td className="tabulaire py-1.5 pr-2">{formaterDate(t.date_debut)}</td>
                <td className="tabulaire py-1.5 pr-2">{t.date_fin ? formaterDate(t.date_fin) : '—'}</td>
                <td className="py-1.5 pr-2">
                  <CaseJustificatif transport={t} peutGerer={peutGerer} enCours={enCours} onBasculer={basculerJustificatif} />
                </td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  {t.verrouillee ? (
                    <Infobulle texte="Cette période a servi à une paie clôturée : elle ne se modifie plus.">
                      <span className="text-xs">🔒</span>
                    </Infobulle>
                  ) : (
                    peutGerer && (
                      <>
                        <button
                          type="button"
                          className="text-xs underline"
                          onClick={() =>
                            setEdition({
                              id: t.id,
                              type: t.type,
                              montant: enEuros(t.montant),
                              dateFin: t.date_fin ?? '',
                            })
                          }
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          className="ml-2 text-xs text-red-700 underline"
                          disabled={enCours}
                          onClick={() => supprimer(t)}
                        >
                          Supprimer
                        </button>
                      </>
                    )
                  )}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
      <p className="text-xs text-encre-500">
        Le montant est le prix du titre. L’école en rembourse la moitié au-delà d’un mi-temps, le quart sinon, sur
        chaque mois du contrat que la période touche : la part se lit dans la paie. Une période sans fin vaut pour les
        mois à venir ; en ajouter une ferme la précédente à la veille. Le justificatif se fournit chaque année scolaire
        : au 1er septembre, il repasse à « absent ».
      </p>
    </div>
  );
}

/**
 * La case Justificatif d'une ligne : cochee, « Fourni » avec la date de
 * remise en infobulle ; decochee, « Absent » — et, si une date subsiste,
 * elle dit l'annee passee pour laquelle il avait ete fourni.
 */
function CaseJustificatif({
  transport: t,
  peutGerer,
  enCours,
  onBasculer,
}: {
  transport: TransportContrat;
  peutGerer: boolean;
  enCours: boolean;
  onBasculer: (t: TransportContrat) => void;
}) {
  const titre = t.justificatif_le
    ? t.justificatif
      ? `Fourni le ${formaterDate(t.justificatif_le)}`
      : `Fourni le ${formaterDate(t.justificatif_le)}, pour une année scolaire passée`
    : 'Aucun justificatif remis';
  return (
    <Infobulle texte={titre} balise="label" className="inline-flex items-center gap-2">
      <input
        type="checkbox"
        className="h-4 w-4"
        aria-label="Justificatif fourni"
        checked={t.justificatif}
        disabled={!peutGerer || enCours}
        onChange={() => onBasculer(t)}
      />
      <span className={t.justificatif ? 'text-encre-900' : 'text-encre-400'}>{t.justificatif ? 'Fourni' : 'Absent'}</span>
    </Infobulle>
  );
}
